import axiosClient from './axios.client.js';

/** Map Host → city package (public). Pass host for local/dev override. */
export async function resolveHostApi(host) {
  const params = host ? { host: String(host) } : undefined;
  return axiosClient.get('/application-type/resolve-host', { params });
}

/** Public discovery funnel ping. */
export async function trackDiscoveryEventApi(body = {}) {
  try {
    return await axiosClient.post('/application-type/discovery-events', body);
  } catch (_) {
    return null;
  }
}

/** City discovery funnel (auth). */
export async function getDiscoveryAnalyticsApi({ packageId, days } = {}) {
  return axiosClient.get('/application-type/discovery-analytics', {
    params: {
      ...(packageId ? { packageId } : {}),
      ...(days ? { days } : {}),
    },
  });
}

/** Full jurisdiction package (catalog + discoveryExtras + intake). */
export async function getJurisdictionPackageApi(jurisdictionId = 'san-francisco') {
  const id = encodeURIComponent(String(jurisdictionId || 'san-francisco').trim() || 'san-francisco');
  const data = await axiosClient.get(`/application-type/jurisdictions/${id}`);
  return data;
}

/** List available jurisdiction packages. */
export async function listJurisdictionsApi() {
  const data = await axiosClient.get('/application-type/jurisdictions');
  return data?.jurisdictions || [];
}

/** Tenant override — requires auth. */
export async function putJurisdictionPackageApi(jurisdictionId, body = {}) {
  const id = encodeURIComponent(String(jurisdictionId || '').trim());
  return axiosClient.put(`/application-type/jurisdictions/${id}`, body);
}

/**
 * Clone package onto the current tenant (default) or globally (scope: 'global').
 */
export async function cloneJurisdictionPackageApi(body = {}) {
  return axiosClient.post('/application-type/jurisdictions/clone', body);
}

/**
 * Provision a city as its own tenant + tenant-scoped package (workspace admin).
 * Body: { id, name?, from?, adminUsername?, adminEmail?, adminPassword?, mapCenter?, ... }
 */
export async function onboardCityApi(body = {}) {
  return axiosClient.post('/application-type/jurisdictions/onboard-city', body);
}

/** Upsert a global jurisdiction package (workspace admin). */
export async function putGlobalJurisdictionPackageApi(jurisdictionId, body = {}) {
  const id = encodeURIComponent(String(jurisdictionId || '').trim());
  return axiosClient.put(`/application-type/jurisdictions/${id}/global`, body);
}

/**
 * Lite city package patch (name, address labels, fee notes, portals, map center).
 * Pass tenantId when super-admin edits another city tenant.
 */
export async function patchJurisdictionLiteApi(jurisdictionId, body = {}) {
  const id = encodeURIComponent(String(jurisdictionId || '').trim());
  return axiosClient.patch(`/application-type/jurisdictions/${id}/lite`, body);
}

/** Public parcel + constraint lookup (SF GIS when package parcelAdapter allows). */
export async function lookupSfParcelApi({
  address, location, lon, lat, propertyType, projectType, jurisdictionId,
} = {}) {
  return axiosClient.post('/application-type/parcel', {
    address: address || location || undefined,
    location: location || address || undefined,
    lon,
    lat,
    propertyType: propertyType || projectType || undefined,
    projectType: projectType || propertyType || undefined,
    jurisdictionId: jurisdictionId || undefined,
  });
}

/** Application-type advise (routing + checklist + fees). */
export async function adviseApplicationTypeApi(body = {}) {
  return axiosClient.post('/application-type/advise', body);
}

/** ZoningCheck-style land-use clearance. */
export async function checkSfLandUseApi(body = {}) {
  return axiosClient.post('/application-type/land-use', body);
}

/** Illustrative fee estimate. */
export async function estimateSfFeesApi(body = {}) {
  return axiosClient.post('/application-type/fees', body);
}

/** Requirements checklist from scoping answers. */
export async function scopeSfRequirementsApi(body = {}) {
  return axiosClient.post('/application-type/scope', body);
}

/** Full project packet (markdown + JSON) for download. */
export async function fetchSfProjectSummaryApi(body = {}) {
  return axiosClient.post('/application-type/project-summary', body);
}

/** Download project summary as .md and .json files in the browser. */
export async function downloadSfProjectSummary(body = {}, { pdf = false, checklistOnly = false } = {}) {
  const data = await fetchSfProjectSummaryApi(body);
  const packet = data?.packet || data;
  if (!packet) throw new Error(data?.message || 'No project summary returned');
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const base = checklistOnly ? `sf-counter-checklist-${stamp}` : `sf-project-summary-${stamp}`;
  let md = packet.markdown || '';
  if (checklistOnly) {
    const docs = packet.routing?.documentsChecklist || body.routing?.documentsChecklist || [];
    const script = packet.routing?.counterVisitScript || body.routing?.counterVisitScript || [];
    const city = packet.jurisdiction?.name || 'City';
    const lines = [
      `# Bring to the counter — ${city}`,
      '',
      `Generated: ${packet.generatedAt || stamp}`,
      '',
      '## Documents checklist',
      ...(docs.length
        ? docs.map((d) => `- [ ] **${d.label}**${d.detail ? ` — ${d.detail}` : ''}`)
        : ['- [ ] Photo ID', '- [ ] Project summary from this session']),
      '',
    ];
    if (script.length) {
      lines.push('## What to say', ...script.map((t, i) => `${i + 1}. ${t}`), '');
    }
    lines.push('---', '', `_${packet.disclaimer || 'Guidance only — not an official determination.'}_`);
    md = lines.join('\n');
    packet.checklistMarkdown = md;
  }
  const mdBlob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
  const jsonBlob = new Blob([JSON.stringify(packet, null, 2)], { type: 'application/json;charset=utf-8' });
  const trigger = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };
  trigger(mdBlob, `${base}.md`);
  if (!checklistOnly) trigger(jsonBlob, `${base}.json`);
  if (pdf && md) {
    try {
      const { printPanelAsPdf } = await import('../utils/panelExport.js');
      const htmlBody = String(md)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>');
      printPanelAsPdf({
        title: checklistOnly ? 'Counter documents checklist' : 'Counter project summary',
        htmlBody,
      });
    } catch (_) {
      /* PDF is optional */
    }
  }
  return packet;
}

/** Persist planner staff session (server). */
export async function saveStaffSessionApi(body = {}) {
  return axiosClient.post('/application-type/staff-sessions', body);
}

/** Catalog CLAW advise for thin intake (Form→Flow→App public path). */
export async function adviseApplicationIntakeApi(body = {}) {
  return axiosClient.post('/application-type/advise-intake', body);
}

/** Upsert durable discovery → intake session. */
export async function saveDiscoverySessionApi(body = {}) {
  const sessionId = body.sessionId;
  if (sessionId) {
    return axiosClient.put(`/application-type/discovery-sessions/${encodeURIComponent(sessionId)}`, body);
  }
  return axiosClient.post('/application-type/discovery-sessions', body);
}

/** Load discovery/intake session. */
export async function getDiscoverySessionApi(sessionId) {
  const data = await axiosClient.get(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}`,
  );
  return data?.session || null;
}

/** Import downloaded advisor packet JSON into a session. */
export async function importDiscoveryPacketApi(sessionId, packet, { packageId } = {}) {
  const data = await axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/import-packet`,
    { packet, packageId },
  );
  return data?.session || null;
}

/** CLAW-style intake chat turn (optional agentLoop = Gemini tool interpretation). */
export async function intakeChatApi(sessionId, message, { agentLoop = true } = {}) {
  const data = await axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/intake-chat`,
    { message, agentLoop: Boolean(agentLoop) },
  );
  return data;
}

/** Create publicSector LNP draft from a ready intake session. */
export async function createLnpDraftApi(sessionId, { force } = {}) {
  const data = await axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/create-lnp-draft`,
    { force: Boolean(force) },
  );
  return data;
}

/** Submit LNP draft for a discovery session. */
export async function submitLnpDraftApi(sessionId) {
  const data = await axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/submit-lnp`,
    {},
  );
  return data;
}

/** Refresh publicSector application status for track/receipt UX. */
export async function getLnpStatusApi(sessionId) {
  const data = await axiosClient.get(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/lnp-status`,
  );
  return data;
}

/** Resolve intake schema for an application type. */
export async function getIntakeSchemaApi({
  jurisdictionId, applicationTypeId, track,
} = {}) {
  const data = await axiosClient.get('/application-type/intake-schema', {
    params: {
      ...(jurisdictionId ? { jurisdictionId } : {}),
      ...(applicationTypeId ? { applicationTypeId } : {}),
      ...(track ? { track } : {}),
    },
  });
  return data?.schema || null;
}

/** Catalog + SoR intake requirements (CLAW if_resolve_intake_requirements). */
export async function resolveIntakeRequirementsApi(body = {}) {
  return axiosClient.post('/application-type/resolve-intake-requirements', body);
}

/** CLAW: ingest attachment (+ optional extract / site-plan review). */
export async function ingestIntakeAttachmentApi(sessionId, body = {}) {
  return axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/ingest-attachment`,
    body,
  );
}

/** CLAW: extract field proposals from a session attachment. */
export async function extractIntakeDocumentApi(sessionId, body = {}) {
  return axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/extract-document`,
    body,
  );
}

/** CLAW: review site plan attachment. */
export async function reviewSitePlanApi(sessionId, body = {}) {
  return axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/review-site-plan`,
    body,
  );
}

/** CLAW: agency policy query with citations (packageId required). */
export async function queryAgencyPolicyApi(body = {}) {
  return axiosClient.post('/application-type/query-agency-policy', body);
}

/** CLAW: packet completeness triage (package via session). */
export async function assessCompletenessApi(sessionId, body = {}) {
  const id = sessionId || body.sessionId;
  if (id) {
    return axiosClient.post(
      `/application-type/discovery-sessions/${encodeURIComponent(id)}/assess-completeness`,
      { ...body, sessionId: id },
    );
  }
  return axiosClient.post('/application-type/assess-completeness', body);
}

/** CLAW: plain-language fees for a package / session. */
export async function explainFeesApi(body = {}) {
  const id = body.sessionId;
  if (id) {
    return axiosClient.post(
      `/application-type/discovery-sessions/${encodeURIComponent(id)}/explain-fees`,
      body,
    );
  }
  return axiosClient.post('/application-type/explain-fees', body);
}

/** CLAW: counter visit brief for session package. */
export async function counterBriefApi(sessionId, body = {}) {
  return axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/counter-brief`,
    { ...body, sessionId },
  );
}

/** CLAW: status / what-happens-next coach. */
export async function statusCoachApi(sessionId) {
  return axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/status-coach`,
    { sessionId },
  );
}

/** CLAW: zoning plain-language (packageId required). */
export async function explainZoningApi(body = {}) {
  return axiosClient.post('/application-type/explain-zoning', body);
}

/** CLAW: deficiency → correction actions. */
export async function planCorrectionsApi(sessionId, body = {}) {
  return axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/plan-corrections`,
    { ...body, sessionId },
  );
}

/** CLAW: suggest inspector from package roster (location + availability). */
export async function suggestInspectionAssignmentApi(body = {}) {
  const id = body.sessionId;
  if (id) {
    return axiosClient.post(
      `/application-type/discovery-sessions/${encodeURIComponent(id)}/suggest-inspection-assignment`,
      body,
    );
  }
  return axiosClient.post('/application-type/suggest-inspection-assignment', body);
}

/** CLAW: assign inspector (package roster; optional SoR push). */
export async function assignInspectionApi(body = {}) {
  const id = body.sessionId;
  if (id) {
    return axiosClient.post(
      `/application-type/discovery-sessions/${encodeURIComponent(id)}/assign-inspection`,
      body,
    );
  }
  return axiosClient.post('/application-type/assign-inspection', body);
}

/** CLAW: inspection prep checklist. */
export async function prepareInspectionApi(sessionId, body = {}) {
  return axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/prepare-inspection`,
    { ...body, sessionId },
  );
}

/** CLAW: multi-permit portfolio for session package. */
export async function permitPortfolioApi(sessionId, body = {}) {
  return axiosClient.post(
    `/application-type/discovery-sessions/${encodeURIComponent(sessionId)}/permit-portfolio`,
    { ...body, sessionId },
  );
}

/** List staff sessions (optional packageId). */
export async function listStaffSessionsApi({ packageId, limit } = {}) {
  const data = await axiosClient.get('/application-type/staff-sessions', {
    params: {
      ...(packageId ? { packageId } : {}),
      ...(limit ? { limit } : {}),
    },
  });
  return data?.sessions || [];
}

/** Load one staff session. */
export async function getStaffSessionApi(id) {
  const data = await axiosClient.get(`/application-type/staff-sessions/${encodeURIComponent(id)}`);
  return data?.session || null;
}
